// --- Work items ---
//
// The legacy Goal record (workspace_goals) was demolished in YAG-508:
// Commitments dissolved per ADR-0024 into Projects/Milestones and the Team's
// Number. `tickets.goal_id` survives in the DB as an inert legacy link, but
// no shared type models it.
/**
 * Canonical work item lifecycle for autonomous desk operations. Legacy board
 * phases are accepted only at API/tool boundaries and normalized before
 * persistence.
 */
export const WORK_ITEM_PHASES = [
    "proposed",
    "accepted",
    "planning",
    "awaiting_execution_approval",
    "executing",
    "needs_input",
    "final_review",
    "done",
    "dismissed",
    /**
     * Split into children (ADR-0044 edge case 30, PR 2 Task 8b). The plan named
     * N pieces, a person confirmed them, and each piece became its own ticket in
     * Plan under the same Team, lane and supervisor. The original closes here
     * rather than at `done` (nothing shipped) or `dismissed` (the work was not
     * abandoned, it was divided), and its children are its record.
     */
    "split",
];
/**
 * The one terminal set for work item phases (handoff reliability P0.3). Every
 * reader that asks "is this item still live?" (claims, sweeps, runs, cases)
 * reads this list, so `split` cannot drop out of one of them again. `split`
 * keeps its `split -> proposed` edge in WORK_ITEM_PHASE_TRANSITIONS: it is
 * terminal for liveness, not for "Back to Proposed".
 */
export const TERMINAL_WORK_ITEM_PHASES = [
    "done",
    "dismissed",
    "split",
];
export function isTerminalWorkItemPhase(phase) {
    if (phase == null)
        return false;
    return TERMINAL_WORK_ITEM_PHASES.includes(phase);
}
export const WORK_ITEM_PRIORITIES = ["normal", "high", "urgent"];
export const WORK_ITEM_KINDS = [
    "investigate",
    "draft",
    "change",
    "monitor",
    "decide",
    // `verify` (Receipts / YAG-128): a carried step that confirms the
    // consequence of prior work (reply received / event exists / payment
    // recorded) at a deterministic check before a Play closes. Carried, never
    // one-shot — a verify that fails parks at needs_input so the desk proposes
    // the next move.
    "verify",
    // `review` (ADR-0032): the case lane. Work that arrives from the world
    // rather than being commissioned — one item per watched pull request, born
    // real (never `proposed`), closed when the pull request closes. Review runs
    // attach to it without claiming it, and gates open on it when a human is
    // needed. This is the kind that gives a Training hold somewhere to wait.
    "review",
    // `harper_job`: the case file of one Harper job run — the run-detail view,
    // mirroring the review case lane. Minted when a run reaches compose (never
    // for quiet no-op days), linked back from harper_job_runs.work_item_id,
    // carrying {jobId, runId, deskId} in sourceRef.
    "harper_job",
];
export const WORK_ITEM_ORIGIN_TYPES = ["agent", "user", "schedule", "webhook"];
export const WORK_ITEM_ATTENTION_TIERS = ["focus", "review"];
export const WORK_ITEM_ASSIGNEE_TYPES = ["human", "agent"];
/**
 * `triage` = surfaced by an agent / ingested from outside, waiting on a
 * human decision about whether it belongs in the workflow.
 * `active` = in the workflow. `phase` governs where.
 * `dismissed` = explicitly removed from the triage list. Idempotent;
 * used by the new POST /api/work-items/:id/dismiss endpoint.
 */
export const WORK_ITEM_TRIAGE_STATUSES = ["triage", "active", "dismissed"];
/**
 * Product placement for a Work Item.
 *
 * `team` rows are the normal cross-team substrate shown on Team pages and the
 * Work page. `inbox` rows are still Work Items, but belong to the Inbox/email
 * lane and must not appear in Team or Work list reads by default.
 */
export const WORK_ITEM_SURFACES = ["team", "inbox"];
/**
 * Provenance label on a ticket. Used by the triage list source-filter chips.
 * Default `'manual'` is what /api/work-items POST creates today; agent-
 * surfaced ingestion paths (Slack, Gmail, GitHub, Sentry, the web /book
 * lead capture, the heartbeat-driven Pulse surface) set this explicitly.
 */
export const WORK_ITEM_SOURCES = [
    "manual",
    "slack",
    "email",
    "gmail",
    "calendar",
    "github",
    "linear",
    "sentry",
    "stripe",
    "hubspot",
    "notion",
    "intercom",
    "web",
    "pulse",
];
export const WORK_ITEM_GATE_KINDS = [
    "approve_worker_instruction",
    "publish_worker_review",
    "accept_proposal",
    "approve_execution_plan",
    "needs_input",
    "review_result",
    // Non-blocking receipt gate (YAG-128 Receipts). The DB CHECK has always
    // included it (migrations.ts 0014) and verifyStep.ts / feed aggregate emit it;
    // the union omitting it was a lie that mapGateRow laundered with an `as` cast.
    "verify_receipt",
    // The commissioning gate (ADR-0024). Signs off on the role-'brief' page bound
    // to a Project's scoping work item: the human red-pens the brief, then
    // resolving this gate commissions the Project. It is the only gate kind whose
    // resolution has a consequence outside the work-item spine, which is why the
    // resolution and `commissionProject` share one transaction.
    "commission_project",
    // The per-milestone gate (ADR-0024 §"After the push"): "Commissioning the
    // Project grants the standing permission; each milestone's tickets still
    // resolve their own gate before they leave." It is ALSO step 5's "second,
    // separate confirm": the push keys on a RESOLVED gate of this kind for the
    // milestone, never on the brief revision the resolution pinned. One kind
    // rather than two because the decision is one decision -- these tickets, this
    // milestone, out to the customer's tracker -- and a milestone with an
    // approved decomposition whose tickets nobody confirmed is a state no person
    // asked for.
    "milestone_decomposition",
    // The generic Job act gate (ADR-0044, Task 5). Every act a Job performs goes
    // through one ladder helper (`workers/jobAuthority.performAct`), and at
    // Supervised that helper mints a gate pre-filled with the act. Most acts map
    // onto a gate kind that already exists (accept a proposal, approve a plan,
    // a review result); the ones that do not -- mark a pull request ready, merge,
    // push a fix, post -- mint this one, whose payload carries `{job, act,
    // evidence, target}` and whose row carries `job_id` and
    // `supervisor_user_id`. One kind rather than one per act, because the surface
    // renders from the payload's act, never from the kind.
    "job_act",
];
/**
 * The commissioning gate's after-line and verb, in ONE place (YAG-338 wave 3).
 *
 * It lives in shared rather than in `feed/consequence.ts` alone because the same
 * Decision renders from two different copy sources today: the Feed's effect line
 * is computed server-side (`feed/aggregate.ts` `gateEffectCopy`) and the case
 * file's comes from the frontend's `consequence.ts`. For the three gate kinds
 * that predate this the two sources drifted into different sentences for the
 * same act. A Decision that says one thing on the Feed and another on the brief
 * is a Decision the person cannot have been said to have made, so this one is
 * defined once and imported by both.
 *
 * ## What the line may and may not claim
 *
 * ADR-0024 is precise about the consequence: commissioning "grants standing
 * permission for the org to argue toward this Project instead of re-arguing the
 * Number each turn". That is the whole of it.
 *
 * It must NOT promise the tickets go anywhere BY ITSELF. "Nothing reaches the
 * customer's tracker before commissioning" is the ADR's rule, and step 5 makes
 * pushing "a second, separate confirm". Naming that confirm became honest when
 * wave 4 built it: the milestone gate's verb is the button, so the line points
 * at something a person can actually find. Before that it pointed at nothing and
 * said so.
 *
 * It must NOT name a cost, a capacity or a credit. ADR-0024: commissioning
 * allocates attention rather than money. ADR-0027 removed the draw disclosure
 * from every gate on every surface, in either unit.
 *
 * House rule: no em-dashes in copy.
 */
export const COMMISSION_PROJECT_CONSEQUENCE = "Commissioning grants the Teams standing permission to argue work toward this Project instead of re-arguing the Number each turn. Nothing reaches your tracker until you confirm the push, milestone by milestone.";
/** The literal-consequence verb. Never "Approve": say what pressing it does. */
export const COMMISSION_PROJECT_VERB = "Commission this Project";
/** The handed-off state a commissioned Project flips to before it settles. */
export const COMMISSION_PROJECT_RECEIPT = "Commissioned. The Teams can argue work toward it from their next turn.";
/**
 * The per-milestone push gate (ADR-0024 §After the push, wave 4).
 *
 * The SECOND confirm, and the only moment in the whole flow when anything leaves
 * YAGNI. So this line is the one that has to be exact about direction: it names
 * the tracker, and it scopes the grant to this milestone's tickets rather than
 * to the Project, because "that is also the cheaper gate to ask for: a person
 * approving eight tickets for the milestone about to start, not forty for a year
 * of work."
 */
export const MILESTONE_PUSH_CONSEQUENCE = "Confirming creates this milestone's tickets in your tracker. Nothing else in the Project moves, and a later re-scope edits these tickets in place rather than closing them.";
/** The literal-consequence verb for the push. It says where the work goes. */
export const MILESTONE_PUSH_VERB = "Push this milestone's tickets";
/** The handed-off state a pushed milestone flips to before it settles. */
export const MILESTONE_PUSH_RECEIPT = "Pushed. The tickets are in your tracker, and a re-scope edits them in place.";
export const WORK_ITEM_GATE_STATUSES = [
    "open",
    "resolved",
    // YAG-239 §6.2: dead value, kept only to mirror the DB CHECK
    // (work_item_gates_status_check, migration 0014, which permits
    // 'open'/'resolved'/'dismissed'). No writer ever emits it: gates mint at
    // 'open' (gates.ts default / verifyStep.ts explicit) and only ever UPDATE to
    // 'resolved', guarded by status = 'open'. A gate is never dismissed -- its
    // work item is; do not read this as a live transition.
    "dismissed",
];
export const REVIEW_FINDING_CONFIDENCES = ["high", "medium", "low"];
export const REVIEW_FINDING_TEST_SUBJECTS = [
    "unit_test",
    "missing_test",
    "other_test",
    "none",
];
/* ── PR Reviews surface (YAG-392) ── */
/**
 * Lifecycle of one `code_review_reports` row — the durable audit record of a
 * single review run. One meaning per value: `held` means a review draft sits
 * behind an OPEN work-item gate; `parked` means the draft was parked without a
 * gate (drop reason carried in `failure`); `failed` means the run itself
 * failed (budget-park included). `premise_stale` means an armed follow-up
 * aborted at dispatch because the condition it was armed on ("all our
 * threads resolved") no longer held — nothing was reviewed and no review
 * posted; the arm re-took at the frontier or awaits the next event.
 * `in_progress` with a stale `updatedAt` is the
 * honest render of a run killed mid-flight — nothing rewrites history.
 */
export const CODE_REVIEW_REPORT_STATUSES = [
    "in_progress",
    "posted",
    "held",
    "parked",
    "premise_stale",
    "skipped_stale",
    "discarded",
    "superseded",
    "cancelled",
    "failed",
];
/**
 * The stored review state of a watched pull request
 * (`code_review_pr_state.agent_review_state`). Mirrors the CHECK added in
 * migration 0131; the backend's `codeReview/prState.ts` re-exports this array
 * rather than declaring a second copy, so the values cannot drift apart.
 *
 * Unlike `ProctorPrStatus` these are stored, not derived: this is the raw
 * machine fact, the derived word is what a surface should lead with.
 */
export const AGENT_REVIEW_STATES = [
    "pending_review",
    "queued",
    "reviewing",
    "comments_posted",
    "approved",
    "approval_advised",
    "needs_follow_up",
];
/**
 * How a work item was tied to the pull-request review record, so a reader can
 * tell how directly the link was written:
 *
 *  - `case`:   the item IS the review case (`code_review_pr_state.work_item_id`,
 *    migration 0159). The most direct link there is.
 *  - `repo`:   `tickets.repo_id` + `tickets.pr_number`, both naming the repo
 *    and the number.
 *  - `pr_url`: the `/owner/name/pull/N` tail of `tickets.pr_url`, for items
 *    whose repo was never pinned (the PR handoff and the MCP link tool both
 *    write the url without the repo id).
 *
 * There is deliberately no bare-number source: PR 481 exists in every
 * repository a workspace has bound, so a number alone can name the wrong
 * review. No match at all is the ordinary answer.
 */
export const WORK_ITEM_REVIEW_MATCH_SOURCES = ["case", "repo", "pr_url"];
/**
 * Where a pull request sits on the reviews page: held for a person, or one
 * state of the paged list. GitHub's terminal and draft states win over what
 * Proctor last did. The listing SQL (`REVIEW_LIST_STATUS_SQL` in the backend's
 * `codeReview/watchedPrSql.ts`) writes the same rule, and an integration test
 * holds the two to each other.
 */
export const REVIEW_LIST_STATUSES = [
    "held",
    "reviewing",
    "follow_up",
    "posted",
    "waiting",
    "draft",
    "merged",
    "closed",
];
/** The Status chip's values: every list status but held, which has its own section. */
export const REVIEW_LIST_FILTERS = REVIEW_LIST_STATUSES.filter((status) => status !== "held");
export function reviewListStatusOf(githubStatus, proctorStatus) {
    if (githubStatus === "merged" || githubStatus === "closed" || githubStatus === "draft") {
        return githubStatus;
    }
    switch (proctorStatus) {
        case "held_for_you":
            return "held";
        case "reviewing":
        case "queued":
            return "reviewing";
        case "needs_follow_up":
        case "failed":
            return "follow_up";
        case "reviewed":
            return "posted";
        case "waiting":
            return "waiting";
    }
}
/* ── the live run surface (YAG-373) ── */
/**
 * How one step of the approved plan is going.
 *
 * `unknown` is a first-class answer and the common one: only a mission-native
 * run records per-step status (`run_steps`). Every other execution path today
 * runs the approved plan without journalling which step it is on, so its steps
 * report `unknown` rather than a plausible tick. A UI that draws a checkmark
 * nobody recorded is lying about what the run did.
 */
export const WORK_RUN_STEP_STATES = ["done", "current", "not_started", "unknown"];
/**
 * Where the steps came from, so the surface can say how much it knows:
 *
 *  - `mission`       — the run's own mission packet + `run_steps`. Real states.
 *  - `approved_plan` — the signed execution contract / confirmed builder plan.
 *    The contract a person approved, with no per-step progress recorded.
 *  - `plan_page`     — the bound role-'plan' page's `## Steps` list, parsed.
 *    The last resort for items whose structured plan is gone.
 */
export const WORK_RUN_STEP_SOURCES = ["mission", "approved_plan", "plan_page"];
/**
 * The dock verbs an ask can recommend, each one the dock draws. A new verb
 * joins this list and the dock in the same change, never one without the
 * other: `continue_anyway` gives a stopped build one more attempt from its
 * saved change, `patch_plan` widens the plan by exactly what the stop
 * listed and continues, and `take_over` hands a stopped fix round's pull
 * request to the person (the ticket sheet's Take it over).
 */
export const GATE_ASK_ACTIONS = [
    "answer",
    "reoffer",
    "stop",
    "continue_anyway",
    "patch_plan",
    "take_over",
];
/** A per-finding decision an editor makes at the deep-review gate. */
export const REVIEW_FINDING_DECISIONS = ["keep", "edited", "removed"];
export const PENDING_ACTION_STATUSES = [
    "pending",
    "approved",
    "rejected",
    "executed",
    "expired",
];
export const PENDING_ACTION_TYPES = [
    "send_email",
    "merge_pr",
    "deploy",
    "delete_data",
    "create_resource",
    "other",
    "tool_approval",
];
/**
 * How many tickets one split may make, and so how many repositories a plan
 * may name (YAG-1058). Anything larger is a Project, not a split. The
 * pi-extension's plan parser keeps its own copy of this number, since it does
 * not depend on this package.
 */
export const MAX_SPLIT_CHILDREN = 12;
//# sourceMappingURL=workItems.js.map